import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Product, FdaRecall, BolEvidence } from '../types/products';
import { ClinicalDrug } from '../types/clinical';
import COOScoreCard from '../components/COOScoreCard';
import MedicarePartB from '../components/chronology/MedicarePartB';
import COOReasoning from '../components/chronology/COOReasoning';
import PathwayTimeline from '../components/chronology/PathwayTimeline';
import EvidenceStack from '../components/chronology/EvidenceStack';
import { buildChronologyModel, buildEvidenceGroups } from '../components/chronology/chronologyAdapters';
import { ChronologyKeyframes } from '../components/chronology/chronologyTokens';
import { API_BASE } from '../../config';
import { useTheme, Tokens } from '../components/ThemeContext';
import { ccpSourceLabel, bilingualSummary } from '../components/utils';

// ============================================================
// TAB DEFINITIONS for the 6-tab product detail layout
// ============================================================
type ProductTab = 'recalls' | 'pricing' | 'alternatives' | 'manufacturer' | 'data-sources';

const PRODUCT_TABS: Array<{ id: ProductTab; label: string; icon: string }> = [
  { id: 'recalls',       icon: '🚨', label: 'Recalls' },
  { id: 'pricing',       icon: '💰', label: 'Pricing Intelligence' },
  { id: 'alternatives',  icon: '💊', label: 'Therapeutic Alternatives' },
  { id: 'manufacturer',  icon: '🏢', label: 'Manufacturer Intel' },
  { id: 'data-sources',  icon: '📊', label: 'Data Sources' },
];

const VALID_TABS = new Set<string>(PRODUCT_TABS.map(t => t.id));

// ============================================================
// HELPER: useMediaQuery hook (same pattern as Products.tsx)
// ============================================================
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

interface ProductDetailProps {
  product: Product | null;
  onBack: () => void;
  onViewPackaging: (productId: number, packageNdc: string) => void;
  onViewClinicalDrug: (id: number) => void;
  onViewManufacturer?: (id: number) => void;
  onViewProduct?: (id: number) => void;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  RiskBadge: React.ComponentType<{ risk: string }>;
  SourceBadge: React.ComponentType<{ source?: string; confidence?: string; scraped_at?: string; compact?: boolean }>;
  SectionSourceIndicator: React.ComponentType<{ sources?: Product["data_sources"]; fields: string[] }>;
  IconArrowLeft: React.ComponentType;
  IconChevron: React.ComponentType;
}

// Returns a progressive color for Made in USA Score (0=foreign/red, 100=US/green)
function madeInUsaScoreToColor(score: number | null, T: Tokens): string {
  if (score === null) return T.textLight;
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#22C55E";
  if (score >= 40) return "#F59E0B";
  if (score >= 20) return "#F97316";
  return "#EF4444";
}

// Returns a subtle gradient background tint matching the score
function madeInUsaScoreToBg(score: number | null): string {
  if (score === null) return "#F8FAFC";
  if (score >= 80) return "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)";
  if (score >= 60) return "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)";
  if (score >= 40) return "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)";
  if (score >= 20) return "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)";
  return "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)";
}

// Secondary directional label derived from Made in USA Score
function madeInUsaDirectionLabel(score: number | null): string {
  if (score === null) return "No data";
  if (score >= 65) return "Likely US-manufactured";
  if (score >= 40) return "Uncertain origin";
  return "Likely foreign-manufactured";
}

// Maps supply_chain_risk string to numeric score for the Risk ring
function riskToNumeric(risk: string | undefined): number {
  switch (risk) {
    case 'low': return 25;
    case 'medium': return 50;
    case 'high': return 75;
    default: return 0;
  }
}

// Returns color for risk score ring (inverse of MIA: green=low risk, red=high risk)
function riskScoreColor(risk: string | undefined, T: Tokens): string {
  switch (risk) {
    case 'low': return T.success;
    case 'medium': return T.warning;
    case 'high': return T.danger;
    default: return T.textLight;
  }
}

// ── Recall helpers ────────────────────────────────────────────────────────────

function recallClassificationColor(classification: string | undefined, T: Tokens): string {
  if (!classification) return T.textLight;
  const c = classification.toLowerCase();
  // Class I: imminent hazard / serious health risk
  if (/class\s*i[^iv]/.test(c) || c === "class i") return T.danger;
  // Class II: may cause adverse health consequences
  if (/class\s*ii[^i]/.test(c) || c === "class ii") return T.warning;
  // Class III: unlikely to cause health consequences
  return "#6B7280";
}

function recallStatusColor(status: string | undefined, T: Tokens): string {
  if (!status) return T.textLight;
  const s = status.toLowerCase();
  if (s === "ongoing") return T.danger;
  if (s === "terminated" || s === "completed") return "#6B7280";
  return T.textLight;
}

function formatRecallDate(dateStr: string | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return dateStr; }
}

function matchLevelLabel(level: string | undefined): string {
  if (level === "product_ndc") return "Matched via product NDC";
  if (level === "package_ndc") return "Matched via package NDC";
  if (level === "text") return "Matched via text search";
  return level || "";
}

// FDA Recall alert card — shown only when the product has linked recalls
// headerless=true when rendered inside RecallAccordion (header is already the trigger button)
function RecallCard({ recalls, headerless = false }: { recalls: FdaRecall[]; headerless?: boolean }) {
  const { T } = useTheme();
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});

  const hasClassI = recalls.some(r => {
    const c = (r.classification || "").toLowerCase();
    return /class\s*i[^iv]/.test(c) || c === "class i";
  });
  const hasOngoing = recalls.some(r => (r.status || "").toLowerCase() === "ongoing");
  const accentColor = (hasClassI || hasOngoing) ? T.danger : T.warning;

  const activeCount = recalls.filter(r => (r.status || "").toLowerCase() === "ongoing").length;

  return (
    <div style={{ background: T.white }}>
      {/* Card header — hidden when used inside the accordion */}
      {!headerless && (
        <div style={{
          background: `${accentColor}12`,
          borderBottom: `1px solid ${accentColor}30`,
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>🚨</span>
          <span style={{ fontWeight: 800, fontSize: 13, color: accentColor, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            FDA Recalls
          </span>
          <span style={{ fontSize: 12, color: T.textMid, marginLeft: 4 }}>
            {recalls.length} recall{recalls.length !== 1 ? "s" : ""}
            {activeCount > 0 && (
              <span style={{ marginLeft: 6, fontWeight: 700, color: T.danger }}>
                · {activeCount} ongoing
              </span>
            )}
          </span>
        </div>
      )}

      {/* Recall rows */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {recalls.map((r, idx) => {
          const classColor = recallClassificationColor(r.classification, T);
          const statusColor = recallStatusColor(r.status, T);
          const isExpanded = !!expanded[idx];
          const reason = r.reason_for_recall || "";
          const isLong = reason.length > 160;

          return (
            <div
              key={r.event_id || idx}
              style={{
                borderTop: idx > 0 ? `1px solid ${T.border}` : undefined,
                padding: "14px 20px",
              }}
            >
              {/* Row header: badges + recall number */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {r.classification && (
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: classColor,
                    background: `${classColor}15`, border: `1px solid ${classColor}40`,
                    borderRadius: 999, padding: "2px 9px", textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>
                    {r.classification}
                  </span>
                )}
                {r.status && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: statusColor,
                    background: `${statusColor}12`, border: `1px solid ${statusColor}35`,
                    borderRadius: 999, padding: "2px 9px",
                  }}>
                    {r.status}
                  </span>
                )}
                {r.recall_number && (
                  <span style={{ fontSize: 11, color: T.textLight, marginLeft: 2 }}>
                    {r.recall_number}
                  </span>
                )}
              </div>

              {/* Reason */}
              {reason && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Reason:{" "}
                  </span>
                  <span style={{ fontSize: 12, color: T.textDark, lineHeight: 1.5 }}>
                    {isExpanded || !isLong ? reason : `${reason.slice(0, 160)}…`}
                  </span>
                  {isLong && (
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      style={{
                        marginLeft: 6, background: "none", border: "none", cursor: "pointer",
                        fontSize: 11, color: T.navy, fontWeight: 600, padding: 0,
                      }}
                    >
                      {isExpanded ? "▲ Less" : "▼ More"}
                    </button>
                  )}
                </div>
              )}

              {/* Meta row: firm + date + match info */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 11, color: T.textMid }}>
                {r.recalling_firm && (
                  <span>
                    <strong style={{ color: T.textDark }}>Firm:</strong> {r.recalling_firm}
                  </span>
                )}
                {r.recall_initiation_date && (
                  <span>
                    <strong style={{ color: T.textDark }}>Initiated:</strong> {formatRecallDate(r.recall_initiation_date)}
                  </span>
                )}
                {r.termination_date && (
                  <span>
                    <strong style={{ color: T.textDark }}>Terminated:</strong> {formatRecallDate(r.termination_date)}
                  </span>
                )}
                {r.matched_ndc && (
                  <span
                    title={matchLevelLabel(r.match_level)}
                    style={{ cursor: "help", borderBottom: `1px dotted ${T.border}` }}
                  >
                    <strong style={{ color: T.textDark }}>NDC:</strong> {r.matched_ndc}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Collapsible accordion wrapper for the RecallCard — closed by default
function RecallAccordion({ recalls }: { recalls: FdaRecall[] }) {
  const { T } = useTheme();
  const [open, setOpen] = useState(false);

  const hasClassI = recalls.some(r => {
    const c = (r.classification || "").toLowerCase();
    return /class\s*i[^iv]/.test(c) || c === "class i";
  });
  const hasOngoing = recalls.some(r => (r.status || "").toLowerCase() === "ongoing");
  const accentColor = (hasClassI || hasOngoing) ? T.danger : T.warning;
  const activeCount = recalls.filter(r => (r.status || "").toLowerCase() === "ongoing").length;

  return (
    <div style={{
      border: `2px solid ${accentColor}`,
      borderRadius: T.radius,
      overflow: "hidden",
      boxShadow: `0 2px 8px ${accentColor}20`,
    }}>
      {/* Accordion trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 20px",
          background: `${accentColor}12`,
          border: "none",
          borderBottom: open ? `1px solid ${accentColor}30` : "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 16 }}>🚨</span>
        <span style={{ fontWeight: 800, fontSize: 13, color: accentColor, textTransform: "uppercase", letterSpacing: "0.08em", flex: 1 }}>
          FDA Recalls
          <span style={{ fontWeight: 400, fontSize: 12, color: T.textMid, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>
            {recalls.length} recall{recalls.length !== 1 ? "s" : ""}
            {activeCount > 0 && (
              <span style={{ marginLeft: 6, fontWeight: 700, color: T.danger }}>· {activeCount} ongoing</span>
            )}
          </span>
        </span>
        <span style={{
          fontSize: 11,
          color: accentColor,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
          display: "inline-block",
        }}>▼</span>
      </button>

      {/* Expanded: the full recall card body */}
      {open && <RecallCard recalls={recalls} headerless />}
    </div>
  );
}

// Legacy COO scorer (Made-in-USA score + Score Breakdown chain) — hidden for now:
// its calc is out of date and superseded by the new PathwayTimeline + COO Reasoning.
// The COO Intelligence block still shows the COO sources / per-source evidence;
// only the score math is suppressed. Flip to re-enable once the calc is refreshed.
const SHOW_LEGACY_COO_SCORE = false;

// ── Data Sources Card ──────────────────────────────────────────────
function DataSourcesCard({
  dataSources,
  product,
  SourceBadge
}: {
  dataSources: Product["data_sources"];
  product: Product;
  SourceBadge: React.ComponentType<{ source?: string; confidence?: string; scraped_at?: string; compact?: boolean }>;
}) {
  const { T } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const openTarget = (rawHash: string) => {
      if (!rawHash) return;
      const scrollToTarget = () => {
        window.setTimeout(() => {
          const el = document.getElementById(rawHash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      };

      if (rawHash === "coo-sources") {
        setIsExpanded(true);
        setExpandedSections({});
        scrollToTarget();
        return;
      }

      if (rawHash === "coo-section-coo_intelligence") {
        setIsExpanded(true);
        setExpandedSections({ coo_intelligence: true });
        scrollToTarget();
        return;
      }

      const sourcePrefix = "coo-section-source_";
      if (rawHash.startsWith(sourcePrefix)) {
        const sourceName = decodeURIComponent(rawHash.slice(sourcePrefix.length));
        setIsExpanded(true);
        setExpandedSections({ [`source_${sourceName}`]: true });
        scrollToTarget();
        return;
      }

      setIsExpanded(true);
      setExpandedSections({});
    };

    const openFromHash = () => {
      const rawHash = window.location.hash.replace("#", "");
      openTarget(rawHash);
    };

    const openFromCustomEvent = (event: Event) => {
      const custom = event as CustomEvent<{ hash?: string }>;
      openTarget(custom.detail?.hash || "");
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    window.addEventListener("coo-open-target", openFromCustomEvent as EventListener);
    return () => {
      window.removeEventListener("hashchange", openFromHash);
      window.removeEventListener("coo-open-target", openFromCustomEvent as EventListener);
    };
  }, []);

  if (!dataSources || Object.keys(dataSources).length === 0) return null;

  const sourceCount = Object.keys(dataSources).length;
  const cooEntries = Object.entries(dataSources).filter(([field]) => (
    field === "country_of_origin" || field.startsWith("coo_")
  ));
  const cooEvidenceList = product.coo_evidences || [];
  const nonCooEntries = Object.entries(dataSources).filter(([field]) => (
    !(field === "country_of_origin" || field.startsWith("coo_"))
  ));

  const groupedBySources = nonCooEntries.reduce((acc, [field, sourceInfo]) => {
    const sourceName = sourceInfo.source || 'UNKNOWN';
    if (!acc[sourceName]) {
      acc[sourceName] = [];
    }
    acc[sourceName].push({ field, ...sourceInfo });
    return acc;
  }, {} as Record<string, Array<{ field: string; source: string; url?: string; confidence?: string; scraped_at?: string }>>);

  const cooAudit = product.coo_scoring_audit && typeof product.coo_scoring_audit === "object"
    ? product.coo_scoring_audit
    : null;
  const cooAdjustments = Array.isArray(cooAudit?.adjustments) ? cooAudit.adjustments : [];

  const pointsBySource = cooAdjustments.reduce((acc: Record<string, number>, adj: any) => {
    const source = (adj?.source || "SCORING").toString();
    const weight = Number(adj?.weight || 0);
    acc[source] = (acc[source] || 0) + weight;
    return acc;
  }, {});

  const evidencesBySource = cooEvidenceList.reduce((acc: Record<string, typeof cooEvidenceList>, ev) => {
    const source = ev.source_type || "UNKNOWN";
    if (!acc[source]) acc[source] = [];
    acc[source].push(ev);
    return acc;
  }, {} as Record<string, typeof cooEvidenceList>);

  const cooSources = Array.from(new Set([
    ...cooEvidenceList.map(ev => ev.source_type || "UNKNOWN"),
    ...cooAdjustments.map((adj: any) => (adj?.source || "SCORING").toString()),
    ...cooEntries.map(([, sourceInfo]) => sourceInfo.source || "UNKNOWN"),
  ])).filter((source) => {
    const hasEvidence = (evidencesBySource[source] || []).length > 0;
    const sourcePoints = pointsBySource[source] || 0;
    return hasEvidence || sourcePoints !== 0;
  });

  const sortedSourceSections = Object.entries(groupedBySources)
    .sort(([a], [b]) => a.localeCompare(b));

  const sourceAccent = (sourceName: string) => {
    const s = sourceName.toUpperCase();
    if (s.includes("NADAC") || s.includes("CMS")) return "#047857";
    if (s.includes("FDA")) return "#1D4ED8";
    if (s.includes("DAILYMED")) return "#7C3AED";
    if (s.includes("RXNORM")) return "#B45309";
    return T.navy;
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sourceSectionId = (sourceName: string) => `coo-section-source_${encodeURIComponent(sourceName)}`;

  return (
    <div id="coo-sources" style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, boxShadow: T.shadow, scrollMarginTop: 16 }}>
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>
            🔍 Data Sources & Provenance
          </h3>
          <span style={{ fontSize: 9, color: T.white, background: T.navy, padding: "3px 8px", borderRadius: 12, fontWeight: 700 }}>
            {sourceCount} fields tracked
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: T.textMid, fontWeight: 600 }}>
            {isExpanded ? "Hide Details" : "Show Details"}
          </span>
          <svg width="16" height="16" viewBox="0 0 16 16" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
            <path d="M4 6l4 4 4-4" stroke={T.navy} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Compact summary */}
      {!isExpanded && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          {Object.entries(dataSources).slice(0, 6).map(([field, sourceInfo]) => (
            <div key={field} style={{ display: "flex", alignItems: "center", gap: 6, background: T.white, border: `1px solid ${T.borderLight}`, borderRadius: T.radiusSm, padding: "6px 10px" }}>
              <span style={{ fontSize: 10, color: T.textLight, fontWeight: 500 }}>{field.replace(/_/g, " ")}</span>
              <SourceBadge source={sourceInfo.source} confidence={sourceInfo.confidence} scraped_at={sourceInfo.scraped_at} compact />
            </div>
          ))}
          {sourceCount > 6 && (
            <div style={{ display: "flex", alignItems: "center", padding: "6px 10px", fontSize: 10, color: T.textMid, fontStyle: "italic" }}>
              +{sourceCount - 6} more...
            </div>
          )}
        </div>
      )}

      {/* Expanded view */}
      {isExpanded && (
        <div style={{ marginTop: 16 }}>
          {(cooEntries.length > 0 || cooEvidenceList.length > 0) && (
            <div id="coo-section-coo_intelligence" style={{ marginBottom: 16, background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow, borderLeft: `4px solid ${T.navy}`, scrollMarginTop: 16 }}>
              <div onClick={() => toggleSection("coo_intelligence")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer", userSelect: "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: T.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>COO Intelligence</span>
                  <span style={{ fontSize: 9, color: T.textLight, background: T.offWhite, padding: "2px 6px", borderRadius: 8 }}>{cooEvidenceList.length} evidences</span>
                </div>
                <span style={{ fontSize: 11, color: T.textMid, fontWeight: 700 }}>{expandedSections["coo_intelligence"] ? "Hide" : "Show"}</span>
              </div>
              {cooSources.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 16px 12px 16px" }}>
                  {cooSources.map((src) => (
                    <span key={src} style={{ fontSize: 9, fontWeight: 700, color: T.navy, background: `${T.navy}10`, border: `1px solid ${T.border}`, borderRadius: 999, padding: "3px 8px" }}>{src}</span>
                  ))}
                </div>
              )}
              {expandedSections["coo_intelligence"] && (
                <div style={{ padding: "0 12px 12px 12px" }}>
                  <COOScoreCard
                    country={product.country_of_origin || null}
                    confidence={product.coo_confidence || null}
                    finalScore={product.coo_scoring_score ?? null}
                    tier={product.coo_scoring_tier || null}
                    scoring={cooAudit ? {
                      baseline:        cooAudit.baseline ?? null,
                      baselineReason:  cooAudit.baseline_reason || null,
                      rawScore:        cooAudit.raw_score ?? null,
                      categoriesCount: cooAudit.categories_count ?? product.coo_scoring_categories ?? null,
                      adjustments:     cooAudit.adjustments || [],
                      ceilingsApplied: cooAudit.ceilings_applied || [],
                      contradictions:  cooAudit.contradictions || [],
                    } : null}
                    evidences={cooEvidenceList.map((ev: any) => ({
                      source:    ev.source_type || "Unknown",
                      country:   ev.country_normalized || null,
                      // Swarm-native fields (post-2026-05-22 fix)
                      factor:    ev.factor || null,
                      hit:       typeof ev.hit === "boolean" ? ev.hit : null,
                      category:  ev.category || null,
                      result:    ev.result || null,
                      rawInputs: ev.raw_inputs || null,
                      // Legacy fields kept for transitional rendering
                      confidence:         ev.confidence || "very_low",
                      score:              null,
                      matchText:          ev.raw_match_text || null,
                      validationNote:     ev.validation_note || null,
                      deductionReasoning: ev.deduction_reasoning || null,
                    }))}
                    primaryDeduction={cooAudit?.primary_deduction || null}
                    hideScore={!SHOW_LEGACY_COO_SCORE}
                  />
                </div>
              )}
            </div>
          )}

          {sortedSourceSections.map(([sourceName, fields]) => {
            const sectionKey = `source_${sourceName}`;
            const accent = sourceAccent(sourceName);
            return (
              <div key={sourceName} id={sourceSectionId(sourceName)} style={{ marginBottom: 12, background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow, borderLeft: `4px solid ${accent}`, scrollMarginTop: 16 }}>
                <div onClick={() => toggleSection(sectionKey)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer", userSelect: "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.05em" }}>{sourceName}</span>
                    <span style={{ fontSize: 9, color: T.textLight, background: T.offWhite, padding: "2px 6px", borderRadius: 8 }}>{fields.length} field{fields.length > 1 ? "s" : ""}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <SourceBadge source={sourceName} confidence={fields[0]?.confidence} compact />
                    <span style={{ fontSize: 11, color: T.textMid, fontWeight: 700 }}>{expandedSections[sectionKey] ? "Hide" : "Show"}</span>
                  </div>
                </div>
                {expandedSections[sectionKey] && (
                  <div style={{ padding: "0 16px 14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {fields.map(({ field, url, confidence, scraped_at }) => (
                      <div key={field} style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px", background: T.offWhite, borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: T.textDark }}>{field.replace(/_/g, " ")}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {confidence && (
                              <span style={{ fontSize: 9, fontWeight: 700, color: confidence === "high" ? T.success : confidence === "medium" ? T.warning : T.danger, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                {confidence} confidence
                              </span>
                            )}
                            {scraped_at && <span style={{ fontSize: 9, color: T.textLight }}>{new Date(scraped_at).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        {url && (
                          <div style={{ fontSize: 10, color: T.textMid, background: T.white, padding: "6px 8px", borderRadius: 4, border: `1px solid ${T.borderLight}`, fontFamily: "monospace", wordBreak: "break-all" }}>
                            <span style={{ color: T.textLight, fontWeight: 600 }}>Source URL: </span>
                            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: T.navy, textDecoration: "none" }}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}>
                              {url}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: 12, padding: 12, background: `linear-gradient(135deg, ${T.navy}05 0%, ${T.gold}05 100%)`, borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
            <div style={{ fontSize: 10, color: T.textMid, lineHeight: 1.6 }}>
              <strong>💡 Data Provenance:</strong> All data is tracked from source to ensure full traceability and compliance.
              URLs represent the exact API calls made to retrieve this information.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Manufacturer Leadership/Ownership types ───────────────────────────────────

interface MfrExecutiveDonation {
  recipient_name: string;
  recipient_party: string;
  amount_usd: number;
  election_cycle?: number;
}

interface MfrExecutive {
  id: number;
  full_name: string;
  title?: string;
  source?: string;
  is_current: boolean;
  donations_dem_usd: number;
  donations_rep_usd: number;
  donations_other_usd: number;
  donations: MfrExecutiveDonation[];
}

interface MfrPoliticianHolding {
  politician_name: string;
  party?: string;
  chamber?: string;
  state?: string;
  transaction_type?: string;
  amount_range?: string;
  transaction_date?: string;
}

interface MfrLeadershipData {
  id: number;
  name: string;
  ccp_flag?: boolean;
  ccp_flag_source?: string;
  ccp_flag_note?: string;
  is_public?: boolean;
  stock_ticker?: string;
  stock_exchange?: string;
  tri_parent_company?: string;
  tri_foreign_parent?: string;
  executives?: MfrExecutive[];
  politician_holdings?: MfrPoliticianHolding[];
}

// ── Product-page tab: Manufacturer Ownership ──────────────────────────────────
function TabMfrOwnership({ data, loading, onViewManufacturer }: {
  data: MfrLeadershipData | null;
  loading: boolean;
  onViewManufacturer?: () => void;
}) {
  const { T } = useTheme();
  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        Loading ownership data…
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        Ownership data not available for this manufacturer.
      </div>
    );
  }

  const parentName = data.tri_parent_company;
  const foreignParent = data.tri_foreign_parent;
  const holdings = data.politician_holdings || [];
  const demHoldings = holdings.filter(h => h.party === 'DEM');
  const repHoldings = holdings.filter(h => h.party === 'REP');

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Context note */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: T.textMid }}>
          Ownership data for <strong style={{ color: T.textDark }}>{data.name}</strong>
        </div>
        {onViewManufacturer && (
          <button
            onClick={onViewManufacturer}
            style={{ fontSize: 11, fontWeight: 700, color: T.navy, background: `${T.navy}08`, border: `1px solid ${T.navy}25`, borderRadius: 999, padding: "4px 12px", cursor: "pointer" }}
          >
            View full manufacturer profile →
          </button>
        )}
      </div>

      {/* Company Status */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px", boxShadow: T.shadow }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
          Company Status
        </div>
        {data.is_public === true && data.stock_ticker ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>
              {data.stock_exchange && <span style={{ fontSize: 11, color: T.textMid, fontWeight: 500, marginRight: 4 }}>{data.stock_exchange}:</span>}
              {data.stock_ticker}
            </span>
            <span style={{ fontSize: 11, color: T.success, background: `${T.success}15`, border: `1px solid ${T.success}40`, borderRadius: 999, padding: "2px 8px", fontWeight: 700 }}>
              Public Company
            </span>
          </div>
        ) : data.is_public === false ? (
          <span style={{ fontSize: 12, color: T.textMid, fontStyle: "italic" }}>Private company — no public SEC filings</span>
        ) : (
          <span style={{ fontSize: 12, color: T.textLight, fontStyle: "italic" }}>Public / private status unknown</span>
        )}
      </div>

      {/* Corporate Structure */}
      {(parentName || foreignParent) && (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px", boxShadow: T.shadow }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
            Corporate Structure
          </div>
          {parentName && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: foreignParent ? `1px solid ${T.borderLight}` : undefined }}>
              <span style={{ fontSize: 12, color: T.textLight }}>Parent Company</span>
              <span style={{ fontSize: 12, color: T.textDark, fontWeight: 600 }}>{parentName}</span>
            </div>
          )}
          {foreignParent && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
              <span style={{ fontSize: 12, color: T.textLight }}>Foreign Parent</span>
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 600 }}>{foreignParent}</span>
            </div>
          )}
        </div>
      )}

      {/* Congressional Stock Holdings */}
      {holdings.length > 0 ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14 }}>🏛️</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textDark, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Congressional Stock Holdings
            </span>
            <span style={{ fontSize: 9, color: T.white, background: T.navy, padding: "2px 7px", borderRadius: 99, fontWeight: 700 }}>
              {holdings.length}
            </span>
            {demHoldings.length > 0 && (
              <span style={{ fontSize: 10, color: "#3B82F6", fontWeight: 700, background: "#3B82F615", borderRadius: 99, padding: "1px 6px" }}>
                {demHoldings.length} DEM
              </span>
            )}
            {repHoldings.length > 0 && (
              <span style={{ fontSize: 10, color: "#EF4444", fontWeight: 700, background: "#EF444415", borderRadius: 99, padding: "1px 6px" }}>
                {repHoldings.length} REP
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {holdings.slice(0, 20).map((h, idx) => {
              const partyColor = h.party === 'DEM' ? "#3B82F6" : h.party === 'REP' ? "#EF4444" : T.textMid;
              return (
                <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 20px", borderTop: idx > 0 ? `1px solid ${T.borderLight}` : undefined }}>
                  <div style={{ width: 28, height: 28, borderRadius: 999, background: `${partyColor}15`, border: `1px solid ${partyColor}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: partyColor, flexShrink: 0 }}>
                    {h.party || "?"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{h.politician_name}</div>
                    <div style={{ fontSize: 11, color: T.textMid, marginTop: 2 }}>
                      {[h.chamber, h.state].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: h.transaction_type === 'Purchase' ? T.success : h.transaction_type === 'Sale' ? T.danger : T.textMid }}>
                      {h.transaction_type}
                    </span>
                    {h.amount_range && <div style={{ fontSize: 10, color: T.textMid, marginTop: 1 }}>{h.amount_range}</div>}
                    {h.transaction_date && <div style={{ fontSize: 10, color: T.textLight }}>{h.transaction_date}</div>}
                  </div>
                </div>
              );
            })}
            {holdings.length > 20 && (
              <div style={{ padding: "8px 20px", fontSize: 11, color: T.textMid, borderTop: `1px solid ${T.borderLight}`, textAlign: "center" }}>
                +{holdings.length - 20} more holdings — view on manufacturer page
              </div>
            )}
          </div>
        </div>
      ) : data.is_public === true ? (
        <div style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.textMid }}>No congressional stock holdings found for {data.stock_ticker}</div>
          <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Source: STOCK Act disclosures via Capitol Trades</div>
        </div>
      ) : null}
    </div>
  );
}

// ── Product-page tab: Manufacturer Leadership ─────────────────────────────────
function TabMfrLeadership({ data, loading, onViewManufacturer }: {
  data: MfrLeadershipData | null;
  loading: boolean;
  onViewManufacturer?: () => void;
}) {
  const { T } = useTheme();
  const [expandedExec, setExpandedExec] = useState<number | null>(null);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        Loading leadership data…
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        Leadership data not available for this manufacturer.
      </div>
    );
  }

  const executives = data.executives || [];
  const totalDem   = executives.reduce((s, e) => s + (e.donations_dem_usd   || 0), 0);
  const totalRep   = executives.reduce((s, e) => s + (e.donations_rep_usd   || 0), 0);
  const totalOther = executives.reduce((s, e) => s + (e.donations_other_usd || 0), 0);
  const totalAll   = totalDem + totalRep + totalOther;
  const demPct     = totalAll > 0 ? (totalDem / totalAll) * 100 : 0;
  const repPct     = totalAll > 0 ? (totalRep / totalAll) * 100 : 0;
  const execsWithDonations = executives.filter(
    e => (e.donations_dem_usd || 0) + (e.donations_rep_usd || 0) + (e.donations_other_usd || 0) > 0
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Context note */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: T.textMid }}>
          Leadership data for <strong style={{ color: T.textDark }}>{data.name}</strong>
        </div>
        {onViewManufacturer && (
          <button
            onClick={onViewManufacturer}
            style={{ fontSize: 11, fontWeight: 700, color: T.navy, background: `${T.navy}08`, border: `1px solid ${T.navy}25`, borderRadius: 999, padding: "4px 12px", cursor: "pointer" }}
          >
            View full manufacturer profile →
          </button>
        )}
      </div>

      {/* CCP flag banner */}
      {data.ccp_flag && (() => {
        const sources = (data.ccp_flag_source || "").split(",").map(s => s.trim()).filter(Boolean);
        const displaySources = sources.map(s => ccpSourceLabel(s)).join(", ");
        return (
          <div style={{ background: "#7F1D1D", borderRadius: T.radius, padding: "14px 20px", display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>🚩</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FECACA", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                CCP / Chinese Military Connection Alert
              </div>
              {data.ccp_flag_note && (
                <div style={{ fontSize: 11, color: "#FCA5A5", marginTop: 3 }}>{bilingualSummary(data.ccp_flag_note)}</div>
              )}
              <div style={{ fontSize: 10, color: "#FCA5A5", marginTop: 3, opacity: 0.8 }}>
                Source: {displaySources || "CCP Screening"} · Classified as high-risk supplier
              </div>
            </div>
          </div>
        );
      })()}

      {/* Company Status */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px", boxShadow: T.shadow }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
          Company Status
        </div>
        {data.is_public === true && data.stock_ticker ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>
              {data.stock_exchange && <span style={{ fontSize: 11, color: T.textMid, fontWeight: 500, marginRight: 4 }}>{data.stock_exchange}:</span>}
              {data.stock_ticker}
            </span>
            <span style={{ fontSize: 11, color: T.success, background: `${T.success}15`, border: `1px solid ${T.success}40`, borderRadius: 999, padding: "2px 8px", fontWeight: 700 }}>
              Public Company
            </span>
          </div>
        ) : data.is_public === false ? (
          <span style={{ fontSize: 12, color: T.textMid, fontStyle: "italic" }}>Private company — no public SEC filings</span>
        ) : (
          <span style={{ fontSize: 12, color: T.textLight, fontStyle: "italic" }}>Public / private status unknown</span>
        )}
      </div>

      {/* Executives list */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14 }}>👥</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textDark, textTransform: "uppercase", letterSpacing: "0.08em" }}>Leadership</span>
          {executives.length > 0 && (
            <span style={{ fontSize: 9, color: T.white, background: T.navy, padding: "2px 7px", borderRadius: 99, fontWeight: 700 }}>
              {executives.length}
            </span>
          )}
        </div>
        {executives.length === 0 ? (
          <div style={{ padding: 24, fontSize: 12, color: T.textMid, textAlign: "center", fontStyle: "italic" }}>
            {data.is_public === false
              ? "Private company — executive data from public filings not available"
              : "No executive data available. Run enrich_executives.py to populate."}
          </div>
        ) : (
          <div>
            {executives.map((exec, idx) => {
              const totalDonations = (exec.donations_dem_usd || 0) + (exec.donations_rep_usd || 0) + (exec.donations_other_usd || 0);
              const isExpanded = expandedExec === exec.id;
              return (
                <div key={exec.id} style={{ borderTop: idx > 0 ? `1px solid ${T.borderLight}` : undefined }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", cursor: totalDonations > 0 ? "pointer" : "default" }}
                    onClick={() => totalDonations > 0 && setExpandedExec(isExpanded ? null : exec.id)}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 999, background: `${T.navy}12`, border: `1px solid ${T.navy}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: T.navy, flexShrink: 0 }}>
                      {exec.full_name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{exec.full_name}</div>
                      <div style={{ fontSize: 11, color: T.textMid, marginTop: 1 }}>
                        {exec.title || "Executive"}
                        {exec.source === 'sec_edgar' && <span style={{ marginLeft: 8, fontSize: 10, color: T.textLight }}>· SEC EDGAR</span>}
                      </div>
                    </div>
                    {totalDonations > 0 && (
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>${totalDonations.toLocaleString()}</div>
                        <div style={{ display: "flex", gap: 4, marginTop: 3, justifyContent: "flex-end" }}>
                          {exec.donations_dem_usd > 0 && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#3B82F6", background: "#3B82F615", borderRadius: 99, padding: "1px 5px" }}>
                              DEM ${exec.donations_dem_usd.toLocaleString()}
                            </span>
                          )}
                          {exec.donations_rep_usd > 0 && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", background: "#EF444415", borderRadius: 99, padding: "1px 5px" }}>
                              REP ${exec.donations_rep_usd.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 9, color: T.textLight, marginTop: 2, display: "block" }}>
                          {isExpanded ? "▲ hide" : "▼ view"}
                        </span>
                      </div>
                    )}
                  </div>
                  {isExpanded && exec.donations.length > 0 && (
                    <div style={{ background: T.offWhite, borderTop: `1px solid ${T.borderLight}`, padding: "8px 20px 12px 20px" }}>
                      {exec.donations.map((d, di) => {
                        const partyColor = d.recipient_party === 'DEM' ? "#3B82F6" : d.recipient_party === 'REP' ? "#EF4444" : T.textMid;
                        return (
                          <div key={di} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: di < exec.donations.length - 1 ? `1px solid ${T.borderLight}` : undefined }}>
                            <div>
                              <span style={{ fontSize: 11, color: T.textDark }}>{d.recipient_name}</span>
                              {d.election_cycle && <span style={{ fontSize: 10, color: T.textLight, marginLeft: 8 }}>{d.election_cycle}</span>}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: partyColor, background: `${partyColor}15`, borderRadius: 99, padding: "1px 6px" }}>
                                {d.recipient_party}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>${d.amount_usd.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Political Influence bar */}
      {execsWithDonations.length > 0 && totalAll > 0 && (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px", boxShadow: T.shadow }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
            Political Influence (FEC)
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 10 }}>
            Total ${totalAll.toLocaleString()} · {execsWithDonations.length} executive{execsWithDonations.length !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 28, marginBottom: 12 }}>
            {demPct > 0 && (
              <div style={{ flex: demPct, background: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", padding: "0 4px" }}>
                {demPct.toFixed(0)}%
              </div>
            )}
            {repPct > 0 && (
              <div style={{ flex: repPct, background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", padding: "0 4px" }}>
                {repPct.toFixed(0)}%
              </div>
            )}
            {totalOther > 0 && (
              <div style={{ flex: (totalOther / totalAll) * 100, background: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", padding: "0 4px" }}>
                {((totalOther / totalAll) * 100).toFixed(0)}%
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 12, flexWrap: "wrap" }}>
            {totalDem   > 0 && <span><span style={{ color: "#3B82F6", fontWeight: 700 }}>●</span> Democratic ${totalDem.toLocaleString()}</span>}
            {totalRep   > 0 && <span><span style={{ color: "#EF4444", fontWeight: 700 }}>●</span> Republican ${totalRep.toLocaleString()}</span>}
            {totalOther > 0 && <span><span style={{ color: "#9CA3AF", fontWeight: 700 }}>●</span> Other ${totalOther.toLocaleString()}</span>}
          </div>
          {demPct > 85 && <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: "#3B82F6" }}>Strong Democratic lean</div>}
          {repPct > 85 && <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: "#EF4444" }}>Strong Republican lean</div>}
        </div>
      )}
    </div>
  );
}

// ── Therapeutic Alternatives types ──────────────────────────────────────────────

type AlternativeProduct = ClinicalDrug['products'][number];

type AltSortKey = 'name' | 'manufacturer' | 'risk' | 'baa' | 'coo' | 'price' | 'shortage';
type AltSortDir = 'asc' | 'desc';

function riskSortValue(risk: string | undefined): number {
  switch (risk) {
    case 'low': return 1;
    case 'medium': return 2;
    case 'high': return 3;
    default: return 4;
  }
}

function getLowestNadac(p: AlternativeProduct): number | null {
  if (!p.packaging || p.packaging.length === 0) return null;
  const prices = p.packaging
    .map(pkg => pkg.nadac_per_unit)
    .filter((v): v is number => v !== undefined && v !== null);
  return prices.length > 0 ? Math.min(...prices) : null;
}

function sortAlternatives(products: AlternativeProduct[], key: AltSortKey, dir: AltSortDir): AlternativeProduct[] {
  const sorted = [...products].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'name':
        cmp = (a.product_name || '').localeCompare(b.product_name || '');
        break;
      case 'manufacturer':
        cmp = (a.manufacturer || '').localeCompare(b.manufacturer || '');
        break;
      case 'risk':
        cmp = riskSortValue(a.supply_chain_risk) - riskSortValue(b.supply_chain_risk);
        break;
      case 'baa':
        cmp = (a.baa_eligible ? 1 : 0) - (b.baa_eligible ? 1 : 0);
        break;
      case 'coo':
        cmp = (a.country_of_origin || a.api_source_country || '').localeCompare(b.country_of_origin || b.api_source_country || '');
        break;
      case 'price': {
        const pa = getLowestNadac(a);
        const pb = getLowestNadac(b);
        if (pa === null && pb === null) cmp = 0;
        else if (pa === null) cmp = 1;
        else if (pb === null) cmp = -1;
        else cmp = pa - pb;
        break;
      }
      case 'shortage':
        cmp = (a.shortage_status || 'None').localeCompare(b.shortage_status || 'None');
        break;
    }
    return cmp;
  });
  return dir === 'desc' ? sorted.reverse() : sorted;
}

// ── Pricing helpers ─────────────────────────────────────────────────────────

function formatNadacDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
}

type PackagingItem = NonNullable<Product['packaging']>[number];

function getProductLowestNadacPkg(packaging: Product['packaging']): PackagingItem | null {
  if (!packaging || packaging.length === 0) return null;
  const withPrice = packaging.filter(pkg => pkg.nadac_per_unit !== undefined && pkg.nadac_per_unit !== null);
  if (withPrice.length === 0) return null;
  return withPrice.reduce((min, pkg) => (pkg.nadac_per_unit! < min.nadac_per_unit!) ? pkg : min);
}

// ── Product-page tab: Enhanced Pricing Intelligence ─────────────────────────────
function TabPricing({
  product,
  clinicalDrug,
  clinicalDrugLoading,
  onViewPackaging,
  onViewClinicalDrug,
  SourceBadge,
  SectionSourceIndicator,
  IconChevron,
  isMobile,
}: {
  product: Product;
  clinicalDrug: ClinicalDrug | null;
  clinicalDrugLoading: boolean;
  onViewPackaging: (productId: number, packageNdc: string) => void;
  onViewClinicalDrug: (id: number) => void;
  SourceBadge: React.ComponentType<{ source?: string; confidence?: string; scraped_at?: string; compact?: boolean }>;
  SectionSourceIndicator: React.ComponentType<{ sources?: Product['data_sources']; fields: string[] }>;
  IconChevron: React.ComponentType;
  isMobile: boolean;
}) {
  const { T } = useTheme();
  const p = product;

  // Compute primary NADAC price (lowest per-unit across all packages)
  const primaryPkg = getProductLowestNadacPkg(p.packaging);
  const primaryPrice = primaryPkg?.nadac_per_unit ?? null;
  const primaryUnit = primaryPkg?.nadac_pricing_unit || 'unit';
  const primaryDate = primaryPkg?.nadac_effective_date;
  const primaryClassification = primaryPkg?.nadac_classification;

  // Package-level stats
  const packagesWithPrice = (p.packaging || []).filter(pkg => pkg.nadac_per_unit !== undefined && pkg.nadac_per_unit !== null);
  const allNadacPrices = packagesWithPrice.map(pkg => pkg.nadac_per_unit!);
  const lowestPrice = allNadacPrices.length > 0 ? Math.min(...allNadacPrices) : null;
  const highestPrice = allNadacPrices.length > 0 ? Math.max(...allNadacPrices) : null;
  const avgPrice = allNadacPrices.length > 0 ? allNadacPrices.reduce((s, v) => s + v, 0) / allNadacPrices.length : null;

  // Alternatives avg from clinical drug data
  const altAvgNadac = clinicalDrug?.stats?.avg_nadac ?? null;
  const altLowestNadac = clinicalDrug?.stats?.lowest_nadac ?? null;
  const altHighestNadac = clinicalDrug?.stats?.highest_nadac ?? null;
  const altProductCount = clinicalDrug?.stats?.total_products ?? 0;

  // Price comparison: is this product cheaper or more expensive than alternatives avg?
  const priceDiffPct = (primaryPrice !== null && altAvgNadac !== null && altAvgNadac > 0)
    ? ((primaryPrice - altAvgNadac) / altAvgNadac) * 100
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Row 1: Hero NADAC Price + Alternatives Comparison ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

        {/* Hero NADAC Price Card */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 24,
          boxShadow: T.shadow,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Gold accent top bar */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.gold }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
              NADAC Per Unit Price
            </h3>
            <SourceBadge source="CMS_NADAC" confidence="high" scraped_at={primaryDate} compact />
          </div>

          {primaryPrice !== null ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 36, fontWeight: 800, color: T.navy, letterSpacing: '-0.02em' }}>
                  ${primaryPrice.toFixed(4)}
                </span>
                <span style={{ fontSize: 14, fontWeight: 500, color: T.textMid }}>
                  per {primaryUnit}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: T.textMid }}>
                {primaryDate && (
                  <span>
                    <strong style={{ color: T.textDark }}>Effective:</strong> {formatNadacDate(primaryDate)}
                  </span>
                )}
                {primaryClassification && (
                  <span>
                    <strong style={{ color: T.textDark }}>Classification:</strong>{' '}
                    <span style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: primaryClassification === 'G' ? `${T.success}12` : `${T.navy}08`,
                      color: primaryClassification === 'G' ? T.success : T.navy,
                      border: `1px solid ${primaryClassification === 'G' ? T.success : T.navy}25`,
                    }}>
                      {primaryClassification === 'G' ? 'Generic' : primaryClassification === 'B' ? 'Brand' : primaryClassification}
                    </span>
                  </span>
                )}
              </div>
              {/* Price range indicator when multiple packages exist */}
              {allNadacPrices.length > 1 && lowestPrice !== null && highestPrice !== null && (
                <div style={{
                  marginTop: 14,
                  padding: '10px 14px',
                  background: T.offWhite,
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.borderLight}`,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                    Price Range Across {allNadacPrices.length} Packages
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.success }}>${lowestPrice.toFixed(4)}</span>
                    <div style={{ flex: 1, height: 4, background: `linear-gradient(90deg, ${T.success}, ${T.warning}, ${T.danger})`, borderRadius: 2, opacity: 0.3 }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.danger }}>${highestPrice.toFixed(4)}</span>
                  </div>
                  {avgPrice !== null && (
                    <div style={{ fontSize: 11, color: T.textMid, marginTop: 4 }}>
                      Average: <strong>${avgPrice.toFixed(4)}</strong> per {primaryUnit}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>💰</div>
              <div style={{ fontSize: 13, color: T.textMid, fontWeight: 500 }}>NADAC pricing not available</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>No NADAC data found for this product's packages</div>
            </div>
          )}

          {/* Data source footer */}
          <div style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: `1px solid ${T.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 10,
            color: T.textLight,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Source: CMS National Average Drug Acquisition Cost (NADAC)
          </div>
        </div>

        {/* Alternatives Price Comparison Card */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 24,
          boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
              Alternatives Comparison
            </h3>
            {clinicalDrug && (
              <span style={{ fontSize: 10, color: T.textLight, fontStyle: 'italic' }}>
                {altProductCount} product{altProductCount !== 1 ? 's' : ''} in class
              </span>
            )}
          </div>

          {clinicalDrugLoading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: T.textMid, fontSize: 12 }}>
              Loading alternatives data…
            </div>
          ) : clinicalDrug && altAvgNadac !== null ? (
            <>
              {/* Primary comparison metric */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: T.textMid, marginBottom: 6 }}>Therapeutic class average (NADAC)</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: T.textDark }}>
                    ${altAvgNadac.toFixed(4)}
                  </span>
                  <span style={{ fontSize: 12, color: T.textMid }}>per unit avg</span>
                </div>
              </div>

              {/* Price difference indicator */}
              {priceDiffPct !== null && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: T.radiusSm,
                  background: priceDiffPct <= 0 ? `${T.success}08` : `${T.danger}08`,
                  border: `1px solid ${priceDiffPct <= 0 ? T.success : T.danger}20`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 16,
                }}>
                  <span style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: priceDiffPct <= 0 ? T.success : T.danger,
                  }}>
                    {priceDiffPct <= 0 ? '▼' : '▲'}
                  </span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: priceDiffPct <= 0 ? T.success : T.danger }}>
                      {Math.abs(priceDiffPct).toFixed(1)}% {priceDiffPct <= 0 ? 'below' : 'above'} average
                    </div>
                    <div style={{ fontSize: 11, color: T.textMid }}>
                      This product vs. therapeutic class average
                    </div>
                  </div>
                </div>
              )}

              {/* Min/Max range */}
              {altLowestNadac !== null && altHighestNadac !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                    <span style={{ fontSize: 12, color: T.textLight }}>Class Lowest</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.success }}>${altLowestNadac.toFixed(4)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                    <span style={{ fontSize: 12, color: T.textLight }}>Class Average</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>${altAvgNadac.toFixed(4)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                    <span style={{ fontSize: 12, color: T.textLight }}>Class Highest</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.danger }}>${altHighestNadac.toFixed(4)}</span>
                  </div>
                </div>
              )}

              {/* Source attribution */}
              <div style={{
                marginTop: 14,
                paddingTop: 10,
                borderTop: `1px solid ${T.borderLight}`,
                fontSize: 10,
                color: T.textLight,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                Source: RxNorm Clinical Drug · NADAC aggregation
              </div>
            </>
          ) : (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>📊</div>
              <div style={{ fontSize: 13, color: T.textMid, fontWeight: 500 }}>No alternatives comparison available</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>
                {!p.clinical_drug_id
                  ? 'This product has no linked clinical drug (RxNorm)'
                  : 'No pricing data available for therapeutic alternatives'
                }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: Package-Level Pricing Breakdown ── */}
      <div style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        overflow: 'hidden',
        boxShadow: T.shadow,
      }}>
        <div style={{
          padding: '16px 24px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
            Package-Level Pricing Breakdown
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: T.textLight }}>
              {(p.packaging || []).length} package{(p.packaging || []).length !== 1 ? 's' : ''}
            </span>
            {packagesWithPrice.length > 0 && (
              <SourceBadge source="CMS_NADAC" confidence="high" compact />
            )}
          </div>
        </div>

        {(p.packaging && p.packaging.length > 0) ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.offWhite }}>
                  <th style={{ padding: isMobile ? '10px 8px' : '10px 16px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Package NDC</th>
                  <th style={{ padding: isMobile ? '10px 8px' : '10px 16px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Description</th>
                  <th style={{ padding: isMobile ? '10px 8px' : '10px 16px', textAlign: 'right', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>NADAC/Unit</th>
                  <th style={{ padding: isMobile ? '10px 8px' : '10px 16px', textAlign: 'center', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Classification</th>
                  <th style={{ padding: isMobile ? '10px 8px' : '10px 16px', textAlign: 'center', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Effective Date</th>
                  {!isMobile && <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, fontSize: 10, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>OTC</th>}
                </tr>
              </thead>
              <tbody>
                {p.packaging.map((pkg, i) => {
                  const hasPrice = pkg.nadac_per_unit !== undefined && pkg.nadac_per_unit !== null;
                  const isLowest = hasPrice && lowestPrice !== null && pkg.nadac_per_unit === lowestPrice && allNadacPrices.length > 1;
                  return (
                    <tr
                      key={`${pkg.package_ndc || 'pkg'}-${i}`}
                      style={{
                        borderBottom: `1px solid ${T.borderLight}`,
                        background: isLowest ? `${T.success}06` : T.white,
                      }}
                    >
                      <td style={{ padding: isMobile ? '10px 8px' : '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            onClick={() => pkg.package_ndc && onViewPackaging(p.id, pkg.package_ndc)}
                            disabled={!pkg.package_ndc}
                            style={{
                              background: 'none', border: 'none', padding: 0, margin: 0,
                              cursor: pkg.package_ndc ? 'pointer' : 'default',
                              color: T.navy, fontSize: 12, fontWeight: 700,
                              textDecoration: 'underline dotted',
                            }}
                            title={pkg.package_ndc ? 'Open packaging detail' : 'No package NDC'}
                          >
                            {pkg.package_ndc || 'N/A'}
                          </button>
                          {pkg.is_sample && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, color: T.warning,
                              background: `${T.warning}12`, borderRadius: 999,
                              padding: '1px 6px', border: `1px solid ${T.warning}30`,
                            }}>SAMPLE</span>
                          )}
                          {isLowest && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, color: T.success,
                              background: `${T.success}12`, borderRadius: 999,
                              padding: '1px 6px', border: `1px solid ${T.success}30`,
                            }}>LOWEST</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: isMobile ? '10px 8px' : '12px 16px', color: T.textMid, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pkg.description || '-'}
                      </td>
                      <td style={{ padding: isMobile ? '10px 8px' : '12px 16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: hasPrice ? T.success : T.textLight }}>
                          {hasPrice ? `$${pkg.nadac_per_unit!.toFixed(4)}` : '-'}
                        </span>
                        {hasPrice && (
                          <div style={{ fontSize: 9, color: T.textLight }}>per {pkg.nadac_pricing_unit || 'unit'}</div>
                        )}
                      </td>
                      <td style={{ padding: isMobile ? '10px 8px' : '12px 16px', textAlign: 'center' }}>
                        {pkg.nadac_classification ? (
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            padding: '2px 8px', borderRadius: 999,
                            background: pkg.nadac_classification === 'G' ? `${T.success}12` : `${T.navy}08`,
                            color: pkg.nadac_classification === 'G' ? T.success : T.navy,
                            border: `1px solid ${pkg.nadac_classification === 'G' ? T.success : T.navy}25`,
                          }}>
                            {pkg.nadac_classification === 'G' ? 'Generic' : pkg.nadac_classification === 'B' ? 'Brand' : pkg.nadac_classification}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: T.textLight }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: isMobile ? '10px 8px' : '12px 16px', textAlign: 'center', fontSize: 11, color: T.textMid }}>
                        {formatNadacDate(pkg.nadac_effective_date)}
                      </td>
                      {!isMobile && (
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {pkg.nadac_otc_indicator ? (
                            <span style={{
                              fontSize: 10, fontWeight: 600,
                              padding: '2px 8px', borderRadius: 999,
                              background: pkg.nadac_otc_indicator === 'Y' ? `${T.navy}08` : T.offWhite,
                              color: pkg.nadac_otc_indicator === 'Y' ? T.navy : T.textLight,
                              border: `1px solid ${T.border}`,
                            }}>
                              {pkg.nadac_otc_indicator === 'Y' ? 'OTC' : 'Rx'}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: T.textLight }}>-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 32, textAlign: 'center', color: T.textMid, fontSize: 12 }}>
            No packaging data available for this product.
          </div>
        )}

        {/* Table footer with source attribution */}
        <div style={{
          padding: '10px 16px',
          background: T.offWhite,
          borderTop: `1px solid ${T.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 10,
          color: T.textLight,
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span>NADAC = National Average Drug Acquisition Cost · Published by CMS</span>
          <span>{packagesWithPrice.length} of {(p.packaging || []).length} packages with pricing</span>
        </div>
      </div>

      {/* ── Row 3: Additional Pricing + Product Info ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

        {/* Other Pricing Sources */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 24,
          boxShadow: T.shadow,
        }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, marginBottom: 16 }}>
            Other Pricing Sources
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.borderLight}` }}>
            <div>
              <span style={{ fontSize: 12, color: T.textDark, fontWeight: 500 }}>Average Wholesale Price (AWP)</span>
              <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>Published benchmark price</div>
            </div>
            <span style={{ fontSize: 14, color: T.textDark, fontWeight: 700 }}>{p.avg_wholesale_price ? `$${p.avg_wholesale_price.toFixed(2)}` : '-'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.borderLight}` }}>
            <div>
              <span style={{ fontSize: 12, color: T.textDark, fontWeight: 500 }}>Federal Supply Schedule (FSS)</span>
              <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>VA/DoD contract price</div>
            </div>
            <span style={{ fontSize: 14, color: p.federal_supply_price ? T.success : T.textLight, fontWeight: 700 }}>
              {p.federal_supply_price ? `$${p.federal_supply_price.toFixed(2)}` : '-'}
            </span>
          </div>

          {/* Source attribution */}
          <div style={{
            marginTop: 14,
            paddingTop: 10,
            borderTop: `1px solid ${T.borderLight}`,
            fontSize: 10,
            color: T.textLight,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Source: FDA NDC Directory · Industry published benchmarks
          </div>
        </div>

        {/* Product Identifiers */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 24,
          boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Product Identifiers</h3>
            <SectionSourceIndicator sources={p.data_sources} fields={['ndc_code', 'dosage_form', 'shortage_status']} />
          </div>
          {[
            { label: 'NDC', value: p.ndc_code },
            { label: 'AHFS Code', value: p.ahfs_code },
            { label: 'Dosage Form', value: p.dosage_form },
            { label: 'Orange Book', value: p.orange_book_listed ? 'Listed' : 'Not Listed' },
            { label: 'FDA Approval', value: p.fda_approval_date },
            { label: 'Shortage Status', value: p.shortage_status || 'None' },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.borderLight}` }}>
              <span style={{ fontSize: 12, color: T.textLight }}>{label}</span>
              <span style={{ fontSize: 12, color: T.textDark, fontWeight: 500 }}>{value || '-'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 4: Equivalents ── */}
      {p.equivalents?.length > 0 && (
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 24,
          boxShadow: T.shadow,
        }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, marginBottom: 12 }}>
            Other Equivalents
          </h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {p.equivalents.map((eq) => (
              <span key={eq} style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: 16, padding: '4px 12px', fontSize: 11, color: T.textMid }}>{eq}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Row 5: Clinical Drug summary card ── */}
      {p.clinical_drug_id && (
        <div style={{ background: `linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)`, border: '1px solid #C4B5FD', borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <h3 style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Clinical Drug (RxNorm)</h3>
                <SectionSourceIndicator sources={p.data_sources} fields={['clinical_drug_id']} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#4C1D95', marginBottom: 4 }}>{p.clinical_drug_name || 'Clinical Drug'}</div>
              <div style={{ fontSize: 12, color: '#7C3AED' }}>
                SCD RxCUI: {p.clinical_drug_id}
                {p.sbd_rxcui && <span style={{ marginLeft: 12 }}>SBD RxCUI: {p.sbd_rxcui}</span>}
              </div>
            </div>
            <button
              onClick={() => onViewClinicalDrug(p.clinical_drug_id!)}
              style={{ padding: '10px 20px', borderRadius: T.radiusSm, border: '2px solid #7C3AED', background: 'rgba(255,255,255,0.8)', color: '#6D28D9', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
            >
              <span>View All Equivalents</span>
              <IconChevron />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Product-page tab: Therapeutic Alternatives ─────────────────────────────────
function TabAlternatives({
  clinicalDrug,
  loading,
  currentProductId,
  onViewProduct,
  ScoreRing,
  isMobile,
}: {
  clinicalDrug: ClinicalDrug | null;
  loading: boolean;
  currentProductId: number;
  onViewProduct?: (id: number) => void;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  isMobile: boolean;
}) {
  const { T } = useTheme();
  const [sortKey, setSortKey] = useState<AltSortKey>('risk');
  const [sortDir, setSortDir] = useState<AltSortDir>('desc');

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        Loading therapeutic alternatives…
      </div>
    );
  }
  if (!clinicalDrug || clinicalDrug.products.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        No therapeutic alternatives available. This product may not have a linked clinical drug (RxNorm).
      </div>
    );
  }

  const alternatives = clinicalDrug.products.filter(p => p.id !== currentProductId);

  if (alternatives.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: T.textMid, fontSize: 13 }}>
        No alternative products found for this clinical drug.
      </div>
    );
  }

  const sorted = sortAlternatives(alternatives, sortKey, sortDir);

  const handleSort = (key: AltSortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'baa' ? 'desc' : 'asc');
    }
  };

  const sortArrow = (key: AltSortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const columns: Array<{ key: AltSortKey; label: string; minWidth?: number }> = [
    { key: 'name',         label: 'Product',      minWidth: 180 },
    { key: 'manufacturer', label: 'Manufacturer',  minWidth: 120 },
    { key: 'risk',         label: 'Risk',          minWidth: 70 },
    { key: 'baa',          label: 'BAA',           minWidth: 60 },
    { key: 'coo',          label: 'COO',           minWidth: 100 },
    { key: 'price',        label: 'NADAC',         minWidth: 90 },
    { key: 'shortage',     label: 'Shortage',      minWidth: 80 },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: T.textMid }}>
          <strong style={{ color: T.textDark }}>{alternatives.length}</strong> alternative{alternatives.length !== 1 ? 's' : ''} for clinical drug{' '}
          <strong style={{ color: T.navyLight }}>{clinicalDrug.name}</strong>
        </div>
        <span style={{ fontSize: 10, color: T.textLight, fontStyle: "italic" }}>
          Click any row to view product detail
        </span>
      </div>

      {/* Table */}
      <div style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        overflow: "hidden",
        boxShadow: T.shadow,
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: T.offWhite }}>
                {columns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      padding: isMobile ? "10px 8px" : "12px 14px",
                      textAlign: "left",
                      fontWeight: 700,
                      color: sortKey === col.key ? T.navy : T.textDark,
                      borderBottom: `2px solid ${sortKey === col.key ? T.navy : T.border}`,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      minWidth: col.minWidth,
                    }}
                  >
                    {col.label}{sortArrow(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const riskScore = riskToNumeric(p.supply_chain_risk);
                const riskColor = riskScoreColor(p.supply_chain_risk, T);
                const lowestPrice = getLowestNadac(p);
                const coo = p.country_of_origin || p.api_source_country || "Unknown";
                const shortage = p.shortage_status || "None";
                const hasShortage = shortage !== "None";

                return (
                  <tr
                    key={p.id}
                    onClick={() => onViewProduct?.(p.id)}
                    style={{
                      borderBottom: `1px solid ${T.borderLight}`,
                      cursor: onViewProduct ? "pointer" : "default",
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.offWhite; }}
                    onMouseLeave={e => { e.currentTarget.style.background = T.white; }}
                  >
                    {/* Product name + NDC */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px" }}>
                      <div style={{ fontWeight: 600, color: T.navy, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? 160 : 240 }}>
                        {p.product_name}
                      </div>
                      <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>
                        NDC: {p.ndc_code}
                        {p.strength && <span> · {p.strength}</span>}
                      </div>
                    </td>

                    {/* Manufacturer */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px", color: T.textMid, fontSize: 12 }}>
                      {p.manufacturer || "Unknown"}
                    </td>

                    {/* Risk Score Ring */}
                    <td style={{ padding: isMobile ? "8px 4px" : "8px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <ScoreRing score={riskScore} size={40} strokeWidth={4} color={riskColor} />
                      </div>
                    </td>

                    {/* BAA */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: p.baa_eligible ? `${T.success}15` : `${T.danger}10`,
                        color: p.baa_eligible ? T.success : T.danger,
                        fontSize: 12,
                        fontWeight: 700,
                      }}>
                        {p.baa_eligible ? '✓' : '✗'}
                      </span>
                    </td>

                    {/* COO */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px" }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "3px 8px",
                        borderRadius: 10,
                        background: coo === "United States" ? T.successBg : coo === "China" ? T.dangerBg : T.warningBg,
                        color: coo === "United States" ? T.success : coo === "China" ? T.danger : T.warning,
                        whiteSpace: "nowrap",
                      }}>
                        {coo}
                      </span>
                    </td>

                    {/* NADAC Price */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px", textAlign: "right" }}>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: lowestPrice !== null ? T.success : T.textLight,
                      }}>
                        {lowestPrice !== null ? `$${lowestPrice.toFixed(4)}` : '-'}
                      </span>
                      {lowestPrice !== null && (
                        <div style={{ fontSize: 9, color: T.textLight }}>per unit</div>
                      )}
                    </td>

                    {/* Shortage */}
                    <td style={{ padding: isMobile ? "10px 8px" : "12px 14px" }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 10,
                        background: hasShortage ? `${T.danger}10` : `${T.success}10`,
                        color: hasShortage ? T.danger : T.success,
                        border: `1px solid ${hasShortage ? T.danger : T.success}30`,
                        whiteSpace: "nowrap",
                      }}>
                        {hasShortage ? `⚠️ ${shortage}` : 'None'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer with count */}
        <div style={{
          padding: "10px 16px",
          background: T.offWhite,
          borderTop: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 11,
          color: T.textLight,
        }}>
          <span>Showing {sorted.length} therapeutic alternative{sorted.length !== 1 ? 's' : ''}</span>
          <span>Source: RxNorm Clinical Drug ({clinicalDrug.clinical_drug_id})</span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function ProductDetailPage({
  product,
  onBack,
  onViewPackaging,
  onViewClinicalDrug,
  onViewManufacturer,
  onViewProduct,
  ScoreRing,
  RiskBadge,
  SourceBadge,
  SectionSourceIndicator,
  IconArrowLeft,
  IconChevron,
}: ProductDetailProps) {
  const { T, getConfidenceColor } = useTheme();
  // ── URL-synced tab state ──
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'recalls';
  const activeTab: ProductTab = VALID_TABS.has(tabParam) ? (tabParam as ProductTab) : 'recalls';

  const setActiveTab = useCallback((tab: ProductTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Accordion: track which sections are expanded (multiple can be open simultaneously)
  const [expandedTabs, setExpandedTabs] = useState<Record<string, boolean>>(() => {
    const explicitTab = searchParams.get('tab');
    if (explicitTab && VALID_TABS.has(explicitTab)) return { [explicitTab]: true };
    return {};
  });

  const toggleTab = useCallback((tab: ProductTab) => {
    setExpandedTabs(prev => ({ ...prev, [tab]: !prev[tab] }));
  }, []);

  // Hooks must be declared before any early returns
  const [mfrData, setMfrData] = useState<MfrLeadershipData | null>(null);
  const [mfrLoading, setMfrLoading] = useState(false);
  const [clinicalDrugData, setClinicalDrugData] = useState<ClinicalDrug | null>(null);
  const [clinicalDrugLoading, setClinicalDrugLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const [rescoreStatus, setRescoreStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [rescoreMessage, setRescoreMessage] = useState('');
  const [apiRiskData, setApiRiskData] = useState<Record<string, any> | null>(null);
  const [apiRiskLoading, setApiRiskLoading] = useState(false);
  const [bolEvidence, setBolEvidence] = useState<BolEvidence | null>(null);
  const navigate = useNavigate();

  // Re-score handler (C5.2)
  const handleRescore = useCallback(async () => {
    if (!product?.ndc_code || rescoreStatus === 'loading') return;
    setRescoreStatus('loading');
    setRescoreMessage('');
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ndcs: [product.ndc_code], priority: 'high' }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        throw new Error(errData.detail || `Error ${resp.status}`);
      }
      const data = await resp.json();
      setRescoreStatus('success');
      setRescoreMessage(data.run_id ? `Queued (${data.run_id.slice(0, 8)}...)` : 'Re-score queued');
      setTimeout(() => setRescoreStatus('idle'), 5000);
    } catch (err: unknown) {
      setRescoreStatus('error');
      setRescoreMessage(err instanceof Error ? err.message : 'Failed to trigger re-score');
      setTimeout(() => setRescoreStatus('idle'), 5000);
    }
  }, [product?.ndc_code, rescoreStatus]);

  // Reset data caches when navigating to a different product (tab persists via URL)
  useEffect(() => {
    setMfrData(null);
    setClinicalDrugData(null);
    setApiRiskData(null);
    setBolEvidence(null);
    setRescoreStatus('idle');
    setRescoreMessage('');
  }, [product?.id]);

  // Fetch Comtrade API risk data for the product
  useEffect(() => {
    if (!product?.id) return;
    setApiRiskLoading(true);
    fetch(`${API_BASE}/metasupply/products/${product.id}/api-risk`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setApiRiskData(d))
      .catch(() => setApiRiskData(null))
      .finally(() => setApiRiskLoading(false));
  }, [product?.id]);

  // Fetch BOL evidence (partner trade-flow tables) for the EvidenceStack.
  useEffect(() => {
    if (!product?.id) return;
    fetch(`${API_BASE}/metasupply/products/${product.id}/bol-evidence`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setBolEvidence(d))
      .catch(() => setBolEvidence(null));
  }, [product?.id]);

  // Lazy-fetch manufacturer data when Manufacturer tab is opened
  useEffect(() => {
    if (!product?.manufacturer_id) return;
    if (!expandedTabs['manufacturer']) return;
    if (mfrData) return;
    setMfrLoading(true);
    fetch(`${API_BASE}/metasupply/manufacturers/${product.manufacturer_id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setMfrData(d))
      .catch(() => {})
      .finally(() => setMfrLoading(false));
  }, [expandedTabs, product?.manufacturer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch clinical drug data when Alternatives tab is opened
  useEffect(() => {
    if (!product?.clinical_drug_id) return;
    if (!expandedTabs['alternatives'] && !expandedTabs['pricing']) return;
    if (clinicalDrugData) return;
    setClinicalDrugLoading(true);
    fetch(`${API_BASE}/metasupply/clinical-drug/${product.clinical_drug_id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setClinicalDrugData(d))
      .catch(() => {})
      .finally(() => setClinicalDrugLoading(false));
  }, [expandedTabs, product?.clinical_drug_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = product;
  if (!p) return null;

  // Internal scorer score: 0 = Made in USA, 100 = fully foreign
  const internalCooScore =
    typeof p.coo_scoring_score === "number"
      ? Math.max(0, Math.min(100, p.coo_scoring_score))
      : null;

  // Made in USA Score: inverted so 100 = perfect US, 0 = fully foreign
  const madeInUsaScore = internalCooScore !== null
    ? Math.round(100 - internalCooScore)
    : null;

  const distributorHqLabel = p.manufacturer_hq || "Not available yet";
  const cooCountryLabel = p.country_of_origin || p.finished_goods_country || "Unknown";
  const showUsFlag = cooCountryLabel === "United States";

  // Score-driven color (green when high/US, red when low/foreign)
  const madeInUsaScoreColor = madeInUsaScoreToColor(madeInUsaScore, T);
  const madeInUsaScoreBg    = madeInUsaScoreToBg(madeInUsaScore);
  const directionLabel      = madeInUsaDirectionLabel(madeInUsaScore);

  const miaScore =
    typeof p.made_in_america_score === "number"
      ? Math.max(0, Math.min(100, Math.round(p.made_in_america_score)))
      : madeInUsaScore ?? 0;
  const miaScoreRingColor = madeInUsaScoreToColor(miaScore, T);

  const riskScore = riskToNumeric(p.supply_chain_risk);
  const riskRingColor = riskScoreColor(p.supply_chain_risk, T);

  const confidenceScore =
    typeof p.confidence_score === "number"
      ? Math.max(0, Math.min(100, Math.round(p.confidence_score)))
      : 0;
  const confidenceRingColor = getConfidenceColor(confidenceScore);

  // High US score (≥80) used for accent styling in the supply chain map
  const isHighUsScore = madeInUsaScore !== null && madeInUsaScore >= 80;

  // Supply Chain Chronology model (pathway + evidence). Cheap pure mappers.
  const chronoModel = buildChronologyModel(p);
  const evidenceGroups = bolEvidence ? buildEvidenceGroups(bolEvidence, chronoModel) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Back button */}
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: T.navy, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, width: "fit-content" }}>
        <IconArrowLeft /> Back to Products
      </button>

      {/* ── 1. Product Header + Score Trio ── */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: "32px 36px", boxShadow: T.shadowMd, position: "relative", overflow: "visible" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${T.red}, ${T.navy}, ${T.gold})`, borderRadius: "16px 16px 0 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 32, flexDirection: isMobile ? "column" : "row" }}>
          {/* Left: product info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", margin: 0 }}>{p.product_name}</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: 15, color: T.textMid }}>
                {p.strength} · {p.dosage_form} ·{" "}
                {p.manufacturer_id && onViewManufacturer ? (
                  <span
                    onClick={() => onViewManufacturer(p.manufacturer_id!)}
                    style={{ color: T.navy, textDecoration: "underline dotted", cursor: "pointer" }}
                    title="View manufacturer profile"
                  >
                    {p.manufacturer}
                  </span>
                ) : (
                  <span>{p.manufacturer}</span>
                )}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.success, background: `${T.success}15`, border: `1px solid ${T.success}40`, borderRadius: 999, padding: "3px 10px" }}>
                {p.product_type || "FDA Approved"}
              </span>
              {p.orange_book_listed && (
                <span style={{ fontSize: 11, fontWeight: 600, color: T.navy, background: `${T.navy}08`, border: `1px solid ${T.border}`, borderRadius: 999, padding: "3px 10px" }}>
                  Orange Book
                </span>
              )}
              {/* Re-score Button (C5.2) — hidden per product owner request */}
            </div>
          </div>

          {/* Score Trio hidden per product owner request */}
        </div>
      </div>

      {/* Compliance Status Bar (COO Verified / Recalls / Shortage) removed */}

      {/* Swarm Agent Rescore banner removed — COO and QRS scores are now
          displayed separately via their own columns on the products table
          and scored-products pages. */}

      {/* ── Reconciler Override Banner ── */}
      {p.reconciler_override_tier && (
        <div style={{
          display: "flex", alignItems: isMobile ? "stretch" : "center",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between", gap: 12,
          padding: isMobile ? "12px 16px" : "14px 20px",
          borderRadius: 12, border: "1px solid #e11d4830",
          background: "linear-gradient(135deg, #e11d4808 0%, #e11d4814 100%)",
          boxShadow: T.shadow,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "#e11d4820", border: "2px solid #e11d48",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e11d48" }}>
                Confidence Override Active
              </div>
              <div style={{ fontSize: 11, color: T.textMid }}>
                Reconciler assessed tier as <strong>{p.reconciler_override_tier}</strong> &mdash;
                scoring agent tier is <strong>{p.coo_scoring_tier || "N/A"}</strong>.
                {p.reconciler_override_reason && <> {p.reconciler_override_reason}</>}
              </div>
            </div>
          </div>
          <span style={{
            padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700,
            background: "#e11d4818", color: "#e11d48", border: "1px solid #e11d4840",
            textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
          }}>
            Override
          </span>
        </div>
      )}

      {/* ── Reconciler Conflict Warning ── */}
      {p.reconciler_conflict && (
        <div style={{
          display: "flex", alignItems: isMobile ? "stretch" : "center",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between", gap: 12,
          padding: isMobile ? "12px 16px" : "14px 20px",
          borderRadius: 12, border: "1px solid #d9770630",
          background: "linear-gradient(135deg, #d9770608 0%, #d9770614 100%)",
          boxShadow: T.shadow,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "#d9770620", border: "2px solid #d97706",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#d97706" }}>
                Reconciler Conflict &mdash; Human Review Recommended
              </div>
              <div style={{ fontSize: 11, color: T.textMid }}>
                LLM disputes the Reconciler&apos;s COO conclusion.
                {p.reconciler_conflict_llm_coo && <> LLM suggests COO should be <strong>{p.reconciler_conflict_llm_coo}</strong>.</>}
                {p.reconciler_conflict_llm_reason && <> &ldquo;{p.reconciler_conflict_llm_reason}&rdquo;</>}
              </div>
            </div>
          </div>
          <span style={{
            padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700,
            background: "#d9770618", color: "#d97706", border: "1px solid #d9770640",
            textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
          }}>
            Conflict
          </span>
        </div>
      )}

      {/* ── Country of Origin Info Row (static display) ── */}
      {(() => {
        const cooCountry = p.country_of_origin || p.finished_goods_country || "Unknown";
        return (
          <div style={{
            borderRadius: 12,
            overflow: "hidden",
            border: `1px solid ${T.border}`,
            boxShadow: T.shadow,
            background: T.white,
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            padding: isMobile ? "14px 16px" : "16px 20px",
            gap: isMobile ? 12 : 0,
          }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>Country of Origin</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: T.navy }}>{cooCountry}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>Confidence Tier</span>
              {(() => {
                const tier = p.coo_scoring_tier;
                if (!tier) return <span style={{ fontSize: 12, fontWeight: 700, color: T.textLight }}>Not Available</span>;
                const upper = tier.toUpperCase();
                const isHigh = upper === 'HIGH' || upper === 'NEAR_CERTAIN';
                const isMod = upper === 'MODERATE' || upper === 'MODERATE_HIGH';
                const tierLabel = isHigh ? 'High' : isMod ? 'Medium' : 'Low';
                const tierColor = isHigh ? '#22C55E' : isMod ? '#F59E0B' : '#EF4444';
                const tierBg = isHigh ? '#22C55E18' : isMod ? '#F59E0B18' : '#EF444418';
                return (
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 12px',
                    borderRadius: 8, background: tierBg, color: tierColor,
                    whiteSpace: 'nowrap',
                  }}>
                    {tierLabel}
                  </span>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* ── Supply Chain Chronology — pathway centerpiece + COO reasoning.
            Keyframes injected once here (CSS keyframes are document-global). ── */}
      <ChronologyKeyframes />
      <PathwayTimeline model={chronoModel} />

      {/* ── Section: COO Reasoning — per-steward verdict list (Supply Chain
            Chronology). Sits between the pathway and the API-supply views. ── */}
      <COOReasoning stewards={p.coo_reasoning} cooCountry={p.country_of_origin} />

      {/* ── Section: Evidence Stack — BOL partner tables in two groups
            (US IMPORT confirmation + API ORIGIN SIGNAL). ── */}
      {evidenceGroups && <EvidenceStack groups={evidenceGroups} apiOrigin={chronoModel.api_origin} />}


      {/* ── Section: API Supply Chain Risk (accordion) ── */}
      {(() => {
        const risk = apiRiskData?.data;
        if (!risk || apiRiskLoading) return null;

        const PATTERN_LABELS: Record<string, string> = {
          structural_rise_china: "Structural Rise — China",
          china_india_substitution: "China→India Substitution",
          reacceleration_after_decline: "Reacceleration After Decline",
          concentration_risk: "Concentration Risk",
          temporary_spike: "Temporary Spike",
          stable_low_dependency: "Stable — Low Dependency",
          stable_high_dependency: "Stable — High Dependency",
          volatility_no_trend: "High Volatility — No Trend",
          india_dominant: "India Dominant",
          diversified: "Diversified",
          insufficient_data: "Insufficient Data",
        };

        const tierColor = (tier: string) => {
          switch (tier) {
            case "CRITICAL": return "#DC2626";
            case "HIGH": return "#DC2626";
            case "MEDIUM": return "#D97706";
            case "LOW": return "#059669";
            default: return T.textLight;
          }
        };

        const tierBg = (tier: string) => {
          switch (tier) {
            case "CRITICAL": return "rgba(220,38,38,0.15)";
            case "HIGH": return "rgba(220,38,38,0.15)";
            case "MEDIUM": return "rgba(217,119,6,0.15)";
            case "LOW": return "rgba(5,150,105,0.15)";
            default: return "rgba(255,255,255,0.1)";
          }
        };

        const chinaPct = Math.round(risk.china_pct || 0);
        const indiaPct = Math.round(risk.india_pct || 0);
        const upstreamConc = Math.min(100, chinaPct + indiaPct);

        const trendLabel = risk.china_trend_precovid > 0 ? "increasing" : risk.china_trend_precovid < 0 ? "decreasing" : "stable";
        const trendArrow = risk.china_trend_precovid > 0 ? "↑" : risk.china_trend_precovid < 0 ? "↓" : "→";

        const seriesChina: Array<{ year: number; pct: number }> = Array.isArray(risk.series_china)
          ? risk.series_china.map((s: any) => ({ year: s.year, pct: s.pct || s.china_pct || 0 }))
          : [];
        const maxPct = seriesChina.length > 0 ? Math.max(...seriesChina.map(s => s.pct), 1) : 1;

        return (
          <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
            <button onClick={() => setExpandedTabs(prev => ({ ...prev, "api-risk": !prev["api-risk"] }))} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: T.white, border: "none", cursor: "pointer", borderBottom: expandedTabs["api-risk"] ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>API Supply — Comtrade Level</span>
                <span style={{
                  display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  color: tierColor(risk.risk_tier), background: tierBg(risk.risk_tier),
                  border: `1px solid ${tierColor(risk.risk_tier)}40`,
                }}>{risk.risk_tier}</span>
                <span style={{ fontSize: 11, color: T.textMid }}>Country-level macro trend (complements the BOL manufacturer tier above)</span>
              </div>
              <span style={{ fontSize: 11, color: T.textMid, transform: expandedTabs["api-risk"] ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▼</span>
            </button>
            {expandedTabs["api-risk"] && (
            <div style={{ padding: isMobile ? "20px 16px" : "28px 32px", background: T.white }}>
            {/* Source label */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke={T.red} strokeWidth="1.5" fill="none"/><path d="M8 4v4l2.5 1.5" stroke={T.red} strokeWidth="1.2" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: "uppercase", letterSpacing: "0.15em" }}>
                UN Comtrade · HS {risk.hs4_code} · {risk.reporter_name} · {risk.year_min}–{risk.year_max}
              </span>
            </div>

            {/* Title */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: T.navy, letterSpacing: "-0.01em", margin: 0 }}>
                API Supply Chain Risk — {p.generic_name || "Unknown"}
              </h3>
            </div>

            {/* Main stat */}
            <p style={{ fontSize: 13, color: T.textMid, marginBottom: 4, lineHeight: 1.5 }}>
              {risk.reporter_name} sources ~{chinaPct}% of this API family from China
            </p>
            {/* Trend subline */}
            <p style={{ fontSize: 12, color: T.textLight, marginBottom: 20, lineHeight: 1.5 }}>
              {trendArrow} China share {trendLabel} since 2019
            </p>

            {/* 3 stat chips */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              <div style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: 8, padding: "14px 16px", borderTop: "3px solid #DC2626" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>China API Share</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, lineHeight: 1, letterSpacing: "-0.02em" }}>{chinaPct}%</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>of total imports</div>
              </div>
              <div style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: 8, padding: "14px 16px", borderTop: "3px solid #D97706" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>India API Share</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, lineHeight: 1, letterSpacing: "-0.02em" }}>{indiaPct}%</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>of total imports</div>
              </div>
              <div style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: 8, padding: "14px 16px", borderTop: "3px solid #7C3AED" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Upstream Concentration</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, lineHeight: 1, letterSpacing: "-0.02em" }}>{upstreamConc}%</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>China + India combined</div>
              </div>
            </div>

            {/* Bar chart: China API share trend by year */}
            {seriesChina.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: T.textMid }}>China API Share by Year</span>
                  <strong style={{ fontSize: 12, color: T.red }}>{chinaPct}% latest</strong>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: isMobile ? 4 : 10, height: 80 }}>
                  {seriesChina.map((s, i) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
                      <span style={{ fontSize: 9, color: T.textMid }}>{Math.round(s.pct)}%</span>
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 60 }}>
                        <div style={{
                          width: 12,
                          height: Math.max(2, (s.pct / maxPct) * 60),
                          borderRadius: "3px 3px 0 0",
                          background: "#DC2626",
                        }} />
                      </div>
                      <span style={{ fontSize: 10, color: T.textLight }}>{s.year}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Narrative block */}
            {risk.narrative_summary ? (
              <div style={{
                background: `${T.danger}08`,
                border: `1px solid ${T.danger}20`,
                borderLeft: "3px solid #DC2626",
                borderRadius: 8,
                padding: "12px 16px",
                marginTop: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#DC2626", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {PATTERN_LABELS[risk.pattern_type] || risk.pattern_type || "Analysis"}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, margin: 0 }}>
                  {risk.narrative_summary}
                </p>
              </div>
            ) : (
              <div style={{
                background: T.offWhite,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: "12px 16px",
                marginTop: 16,
              }}>
                <p style={{ fontSize: 12, color: T.textLight, lineHeight: 1.6, margin: 0 }}>
                  Data generating...
                </p>
              </div>
            )}

            {/* Context paragraph with narrative_short_term */}
            {risk.narrative_short_term && (
              <p style={{ fontSize: 12, color: T.textMid, lineHeight: 1.7, marginTop: 12 }}>
                {risk.narrative_short_term}
              </p>
            )}

            {/* Displacement / acceleration flag alerts */}
            {(risk.displacement_flag || risk.acceleration_flag) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                {risk.displacement_flag && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: `${T.danger}08`, border: `1px solid ${T.danger}20` }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.danger }}>India displacement detected — India share rising as China declines</span>
                  </div>
                )}
                {risk.acceleration_flag && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: `${T.danger}08`, border: `1px solid ${T.danger}20` }}>
                    <span style={{ fontSize: 14 }}>🚀</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.danger }}>Acceleration detected — China share increasing faster than historical trend</span>
                  </div>
                )}
              </div>
            )}

            {/* Refresh indicator */}
            {risk.narrative_needs_refresh && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 12, animation: "spin 2s linear infinite", display: "inline-block" }}>⟳</span>
                <span style={{ fontSize: 10, color: T.textLight }}>Enrichment queued — narrative updating</span>
              </div>
            )}

            {/* CTA + disclaimer row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 10 }}>
              <button
                onClick={() => navigate(`/apis?reporter=${risk.reporter_code}&hs4=${risk.hs4_code}`)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  borderRadius: 8,
                  background: `${T.navy}10`,
                  border: `1px solid ${T.navy}30`,
                  color: T.navy,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Full API Analysis →
              </button>
              <span style={{ fontSize: 10, color: T.textLight }}>
                Source:{" "}
                <a
                  href="https://comtradeplus.un.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: T.navy, textDecoration: "none", fontWeight: 600 }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  UN Comtrade Plus
                </a>{" "}
                · HS Ch.{risk.hs4_code?.slice(0, 2)} · Estimated from bilateral trade flows
              </span>
            </div>
            </div>
            )}
          </div>
        );
      })()}

      {/* ── Section: Recalls (accordion) ── */}
      {(() => {
        const recallCount = Array.isArray(p.recalls) ? p.recalls.length : 0;
        return (
          <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
            <button onClick={() => toggleTab('recalls')} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: T.white, border: "none", cursor: "pointer", borderBottom: expandedTabs['recalls'] ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Recalls</span>
                {recallCount > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: T.white, background: T.danger, borderRadius: 99, padding: "1px 7px", minWidth: 18, textAlign: "center" }}>{recallCount}</span>
                )}
              </div>
              <span style={{ fontSize: 11, color: T.textMid, transform: expandedTabs['recalls'] ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▼</span>
            </button>
            {expandedTabs['recalls'] && (
              <div style={{ padding: 20, background: T.white }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {(p.recalls && p.recalls.length > 0) ? (
                    <RecallCard recalls={p.recalls} />
                  ) : (
                    <div style={{
                      background: T.white,
                      border: `1px solid ${T.border}`,
                      borderRadius: T.radius,
                      padding: 48,
                      textAlign: "center",
                      boxShadow: T.shadow,
                    }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.success, marginBottom: 4 }}>No FDA Recalls</div>
                      <div style={{ fontSize: 12, color: T.textMid }}>
                        This product has no FDA recall history on record.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Pricing Intelligence accordion hidden per product owner request */}

      {/* Therapeutic Alternatives accordion hidden per product owner request */}

      {/* ── Section: Manufacturer Intel (accordion) ── */}
      <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
        <button onClick={() => toggleTab('manufacturer')} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: T.white, border: "none", cursor: "pointer", borderBottom: expandedTabs['manufacturer'] ? `1px solid ${T.border}` : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Manufacturer Intel</span>
            {mfrData?.ccp_flag && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.danger, background: `${T.danger}10`, border: `1px solid ${T.danger}30`, borderRadius: 99, padding: "1px 8px" }} title="Chinese Military Company (DoD 1260H)">CCP Flag</span>
            )}
          </div>
          <span style={{ fontSize: 11, color: T.textMid, transform: expandedTabs['manufacturer'] ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▼</span>
        </button>
        {expandedTabs['manufacturer'] && (
          <div style={{ padding: 20, background: T.white }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <TabMfrOwnership
                data={mfrData}
                loading={mfrLoading}
                onViewManufacturer={onViewManufacturer && p.manufacturer_id ? () => onViewManufacturer!(p.manufacturer_id!) : undefined}
              />
              {mfrData && !mfrLoading && (
                <>
                  <div style={{ height: 1, background: T.border }} />
                  <TabMfrLeadership
                    data={mfrData}
                    loading={false}
                    onViewManufacturer={onViewManufacturer && p.manufacturer_id ? () => onViewManufacturer!(p.manufacturer_id!) : undefined}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Section: Medicare Part B (ASP) reimbursement — Supply Chain
            Chronology block. Collapsed by default; secondary/low-priority. ── */}
      <MedicarePartB data={p.part_b} />

      {/* ── Section: Data Sources & COO Intelligence (uses DataSourcesCard
            which handles its own expand state + nested COO sub-section) ── */}
      <DataSourcesCard
        dataSources={p.data_sources}
        product={p}
        SourceBadge={SourceBadge}
      />

      {/* Footer */}
      <div style={{ fontSize: 11, color: T.textLight, textAlign: "right" }}>
        Last updated: {p.last_updated} - ID: {p.id}
      </div>
    </div>
  );
}
